import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Box, IconButton, Typography } from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid";
import type { TFunction } from "i18next";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { SystemDataGrid } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import type { SystemGridColDef } from "../../../../components/UIToolkit/Tables/SystemDataGrid/types";
import type { EmployeeBucket, RecurringCandidateRow } from "../../../../services/backend.service";
import { FONT_SIZE, INK, STATUS } from "../../../../theme/tokens";
import type { MultiSortOption, SortingState } from "../../../../utils/multiColumnSorting";
import { RecurringShiftCalendarDialog } from "./RecurringShiftCalendarDialog";
import {
	FLAG_TONES,
	flagDescription,
	flagLabel,
	formatIsoDate,
	formatPatternDays,
	formatShare,
	formatSlots,
	NOT_SET,
	patternKindLabel,
	SHIFT_SOURCE_LABELS,
	shiftSourceDescription,
	skipReasonDescription,
	skipReasonLabel,
} from "./recurringDisplay";

type Props = {
	bucket: EmployeeBucket;
	rows: RecurringCandidateRow[];
	/** Candidate ids the operator has kept. Absent for the skipped bucket, which cannot be approved. */
	selectedIds?: string[];
	onSelectionChange?: (ids: string[]) => void;
	minimumScore: number;
	departmentId: string | undefined;
	/** The window the proposal was calculated over — the calendar shows history over the same span. */
	windowMonths: number;
};

/**
 * Detail-panel geometry.
 *
 * The panel is pinned to the grid's left edge and capped to a reading measure rather than stretched
 * to the row's full scroll width: the row is wider than the viewport whenever the columns overflow
 * (measured: a 1730px row inside a 1150px viewport), so a full-width panel puts half its own
 * explanation behind a horizontal scrollbar — and prose at 1730px is unreadable even when it fits.
 */
const DETAIL_MAX_WIDTH = 880;
const DETAIL_VERTICAL_PADDING = 16;
const DETAIL_LINE_HEIGHT = 20;
const DETAIL_ROW_GAP = 6;
/** Wrap estimate at `DETAIL_MAX_WIDTH` in the secondary type size. Only feeds the height calc. */
const DETAIL_CHARS_PER_LINE = 115;

/**
 * A score at or above the operator's own threshold reads as success; below it, as a warning. Deliberately
 * relative to their choice rather than a fixed band — the whole point of the setting is that they decide
 * what "good enough" means.
 */
const scoreColor = (score: number | null, minimumScore: number): string => {
	if (score === null) return INK.MUTED;
	return score >= minimumScore ? STATUS.SUCCESS.main : STATUS.WARNING.main;
};

const ScoreCell: FC<{ score: number | null; minimumScore: number }> = ({ score, minimumScore }) => (
	<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.SECONDARY, color: scoreColor(score, minimumScore), fontVariantNumeric: "tabular-nums" }}>
		{score === null ? NOT_SET : score.toFixed(2)}
	</Typography>
);

const FlagsCell: FC<{ flags: RecurringCandidateRow["flags"]; t: TFunction }> = ({ flags, t }) => {
	if (flags.length === 0) return <Typography sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY }}>{NOT_SET}</Typography>;
	return (
		<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", py: 0.5 }}>
			{flags.map((flag) => (
				<MetaChip key={flag} label={flagLabel(flag, t)} tone={FLAG_TONES[flag]} tooltip={flagDescription(flag, t)} />
			))}
		</Box>
	);
};

/**
 * The reasoning behind one row, as data.
 *
 * Rendering and the height calculation read the SAME list, because MUI needs an explicit pixel
 * height for a detail row (auto-height attaches a per-row ResizeObserver that thrashes the grid).
 * Two parallel lists would drift the moment a line is added, and the visible symptom would be
 * clipped text rather than a type error.
 */
type DetailEntry = { key: string; label: string; text: string };

function detailEntries(row: RecurringCandidateRow, t: TFunction): DetailEntry[] {
	const entries: DetailEntry[] = [];
	if (row.skipReason) {
		entries.push({ key: "skip", label: skipReasonLabel(row.skipReason, t), text: skipReasonDescription(row.skipReason, t) });
	}
	if (row.basedOn) {
		const agreement = row.sourceMatch === null ? "" : ` The two sources agreed on ${formatShare(row.sourceMatch)} of days.`;
		entries.push({
			key: "source",
			label: `Based on ${SHIFT_SOURCE_LABELS[row.basedOn].toLowerCase()}`,
			text: `${shiftSourceDescription(row.basedOn, row.sourceMatch, t)}${agreement}`,
		});
	}
	if (row.runnerUpLabel) {
		const score = row.runnerUpScore === null ? "" : ` (scored ${row.runnerUpScore.toFixed(2)})`;
		entries.push({ key: "runnerUp", label: "Runner-up", text: `${row.runnerUpLabel}${score}` });
	}
	if (row.anchor) {
		entries.push({ key: "anchor", label: "Cycle anchored on", text: formatIsoDate(row.anchor) });
	}
	if (row.location) {
		entries.push({
			key: "location",
			label: "Staffing-grid location",
			text: row.locationInferred ? `${row.location} — deduced, no explicit mapping for this role` : row.location,
		});
	}
	for (const flag of row.flags) {
		entries.push({ key: `flag-${flag}`, label: flagLabel(flag, t), text: flagDescription(flag, t) });
	}
	return entries;
}

/** The reasoning behind one row, opened on demand so the grid itself stays readable. */
const CandidateDetail: FC<{ row: RecurringCandidateRow; t: TFunction }> = ({ row, t }) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			gap: `${DETAIL_ROW_GAP}px`,
			// Sticky so the panel stays at the left edge while the columns scroll under it, and capped
			// so the prose keeps a readable measure instead of stretching to the row's full width.
			position: "sticky",
			left: 0,
			maxWidth: DETAIL_MAX_WIDTH,
			textAlign: "left",
			whiteSpace: "normal",
		}}
	>
		{detailEntries(row, t).map((entry) => (
			<Typography key={entry.key} sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: `${DETAIL_LINE_HEIGHT}px`, color: INK.SECONDARY }}>
				<strong>{entry.label}:</strong> {entry.text}
			</Typography>
		))}
	</Box>
);

/** Lines the panel will occupy once the prose wraps at `DETAIL_MAX_WIDTH`. */
const detailLineCount = (row: RecurringCandidateRow, t: TFunction): number =>
	detailEntries(row, t).reduce((lines, entry) => lines + Math.max(1, Math.ceil((entry.label.length + entry.text.length + 2) / DETAIL_CHARS_PER_LINE)), 0);

/**
 * Columns the search box matches on — passed to the grid AND used by `matchesSearch` below, so the
 * two cannot drift. `SystemDataGrid` filters a `keyof T` spec as a case-insensitive substring of
 * `String(row[key] ?? "")`; `matchesSearch` reproduces exactly that and nothing else, because the
 * selection merge is only correct while "visible here" means the same thing as "visible there".
 */
const SEARCHABLE_FIELDS = ["nurseName", "role", "jobName"] as const satisfies ReadonlyArray<keyof RecurringCandidateRow>;

const matchesSearch = (row: RecurringCandidateRow, query: string): boolean => {
	if (!query.trim()) return true;
	const needle = query.trim().toLowerCase();
	return SEARCHABLE_FIELDS.some((field) =>
		String(row[field] ?? "")
			.toLowerCase()
			.includes(needle),
	);
};

/**
 * Sortable axes per bucket.
 *
 * Score leads for Ready and Needs review, and is deliberately absent from Skipped: a skipped row was
 * never fitted, so its score is `null` on every row there — offering a sort that cannot reorder
 * anything is worse than not offering it. Skipped gets the axes that do vary instead.
 *
 * `sortNullUndefinedLast` on score keeps unscored rows at the bottom in BOTH directions, so flipping
 * to descending doesn't fill the top of the table with blanks.
 */
const SCORED_SORT_OPTIONS: MultiSortOption<RecurringCandidateRow>[] = [
	{ label: "Score", sortColumns: ["score"], sortNullUndefinedLast: true },
	{ label: "Employee", sortColumns: ["nurseName"] },
	{ label: "Days worked", sortColumns: ["workedDayCount"] },
	{ label: "Agreement", sortColumns: ["sourceMatch"], sortNullUndefinedLast: true },
	{ label: "Job", sortColumns: ["jobName"] },
	{ label: "Role", sortColumns: ["role"] },
];

const SKIPPED_SORT_OPTIONS: MultiSortOption<RecurringCandidateRow>[] = [
	{ label: "Days worked", sortColumns: ["workedDayCount"] },
	{ label: "Employee", sortColumns: ["nurseName"] },
	{ label: "Why it was skipped", sortColumns: ["skipReason"] },
	{ label: "Job", sortColumns: ["jobName"] },
	{ label: "Role", sortColumns: ["role"] },
];

/**
 * Weakest proposals first.
 *
 * This screen is a review surface, not a leaderboard: with 139 Ready rows pre-ticked for bulk
 * approval, the ones worth a human's attention are the ones nearest the threshold, not the perfect
 * 1.00s. Ascending puts those on top. One header click flips it for anyone who'd rather see the best
 * first — the point is that score is the axis, and the risky end is where the table opens.
 */
const SCORED_DEFAULT_SORT: SortingState<RecurringCandidateRow>[] = [{ sortColumns: ["score"], sortOrder: "asc", label: "Score", sortNullUndefinedLast: true }];

/** Skipped has no score, so the surprising rows are the ones with the MOST history behind the skip. */
const SKIPPED_DEFAULT_SORT: SortingState<RecurringCandidateRow>[] = [{ sortColumns: ["workedDayCount"], sortOrder: "desc", label: "Days worked" }];

/**
 * One bucket's worth of proposals.
 *
 * Ready and Needs review are selectable — approving is per employee, not per bucket, so a reviewer can
 * keep the eight people whose pattern is obviously right and leave the ninth. Skipped is read-only:
 * there is no pattern on those rows to create.
 */
export const RecurringCandidatesTable: FC<Props> = ({ bucket, rows, selectedIds, onSelectionChange, minimumScore, departmentId, windowMonths }) => {
	const { t } = useTranslation();
	const isSkipped = bucket === "skipped";
	const selectable = !isSkipped && onSelectionChange !== undefined;
	// Search is controlled so the selection merge can tell which rows are on screen. The grid still
	// does its own filtering from the same value; this only mirrors it.
	const [search, setSearch] = useState("");
	const [calendarRow, setCalendarRow] = useState<RecurringCandidateRow | null>(null);
	const visibleIds = useMemo(() => new Set(rows.filter((row) => matchesSearch(row, search)).map((row) => row.candidateId)), [rows, search]);

	const columns = useMemo<SystemGridColDef<RecurringCandidateRow>[]>(() => {
		const calendarColumn: SystemGridColDef<RecurringCandidateRow> = {
			field: "__calendar",
			headerName: "",
			width: 56,
			sortable: false,
			disableExport: true,
			excludeFromColumnPicker: true,
			hideHeaderSeparator: true,
			// Only rows with a pattern get one: a skipped row has nothing to draw, and an empty
			// calendar would read as "this person works nothing" rather than "nothing was proposed".
			renderCell: ({ row }) =>
				row.patternKind ? (
					<IconButton
						size="small"
						aria-label={`Show ${row.nurseName}'s proposed shifts on a calendar`}
						onClick={(event) => {
							// The row is selectable and expandable; without this the click also toggles those.
							event.stopPropagation();
							setCalendarRow(row);
						}}
						data-testid={`recurring-calendar-button-${row.candidateId}`}
					>
						<CalendarMonthIcon fontSize="small" />
					</IconButton>
				) : null,
		};

		const identity: SystemGridColDef<RecurringCandidateRow>[] = [
			// Left of the name on purpose: it acts on the employee in that row, so it belongs beside the
			// thing it acts on rather than at the far end of eleven columns behind a horizontal scroll.
			...(isSkipped ? [] : [calendarColumn]),
			{ field: "nurseName", headerName: "Employee", minWidth: 180, flex: 1.2, excludeFromColumnPicker: true },
			{ field: "role", headerName: "Role", minWidth: 110, flex: 0.8, valueGetter: (_, row) => row.role ?? NOT_SET },
			{ field: "jobName", headerName: "Job", minWidth: 110, flex: 0.8, valueGetter: (_, row) => row.jobName ?? NOT_SET },
		];

		if (isSkipped) {
			return [
				...identity,
				{
					field: "skipReason",
					headerName: "Why it was skipped",
					minWidth: 200,
					flex: 1.4,
					headerTooltip: "Every skipped employee gets a reason — none are dropped silently.",
					renderCell: ({ row }) =>
						row.skipReason ? (
							<MetaChip label={skipReasonLabel(row.skipReason, t)} tone="neutral" tooltip={skipReasonDescription(row.skipReason, t)} />
						) : (
							NOT_SET
						),
					getExportValue: (row) => (row.skipReason ? skipReasonLabel(row.skipReason, t) : ""),
				},
				{ field: "workedDayCount", headerName: "Days worked", width: 120, type: "number", headerTooltip: "Days with a shift in the history window." },
			];
		}

		return [
			...identity,
			{
				field: "patternLabel",
				headerName: "Pattern",
				minWidth: 150,
				flex: 1,
				valueGetter: (_, row) => row.patternLabel ?? NOT_SET,
				headerTooltip: "The repeating shape the fit settled on.",
			},
			{
				field: "patternKind",
				headerName: "Kind",
				width: 120,
				valueGetter: (_, row) => patternKindLabel(row.patternKind, row.cycleWeeks),
			},
			{
				field: "weekdays",
				headerName: "Days",
				minWidth: 160,
				flex: 1.1,
				sortable: false,
				valueGetter: (_, row) => formatPatternDays(row),
				headerTooltip: "The days the template will land on. A rotation drifts, so it shows work/rest counts instead.",
			},
			{
				field: "shiftTimes",
				headerName: "Shift times",
				minWidth: 130,
				flex: 0.9,
				sortable: false,
				valueGetter: (_, row) => formatSlots(row.shiftTimes),
			},
			{
				field: "score",
				headerName: "Score",
				width: 90,
				type: "number",
				headerTooltip: "Measured on the last two weeks of history, which the fit never saw. 1.00 is a perfect reproduction.",
				renderCell: ({ row }) => <ScoreCell score={row.score} minimumScore={minimumScore} />,
				getExportValue: (row) => row.score,
			},
			{
				field: "basedOn",
				headerName: "Source",
				width: 150,
				valueGetter: (_, row) => (row.basedOn ? SHIFT_SOURCE_LABELS[row.basedOn] : NOT_SET),
				headerTooltip: "Which history the fit trusted — decided per employee, not set by you.",
			},
			{
				field: "sourceMatch",
				headerName: "Agreement",
				width: 110,
				type: "number",
				headerTooltip: "How much the schedule and the punches agreed. Low agreement is why a source was dropped.",
				valueGetter: (_, row) => row.sourceMatch,
				renderCell: ({ row }) => formatShare(row.sourceMatch),
			},
			{ field: "workedDayCount", headerName: "Days worked", width: 120, type: "number" },
			{
				field: "flags",
				headerName: "Flags",
				minWidth: 200,
				flex: 1.2,
				sortable: false,
				renderCell: ({ row }) => <FlagsCell flags={row.flags} t={t} />,
				getExportValue: (row) => row.flags.map((flag) => flagLabel(flag, t)).join(", "),
			},
		];
	}, [isSkipped, minimumScore, t]);

	return (
		<>
			<SystemDataGrid<RecurringCandidateRow>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.candidateId}
				searchableColumns={[...SEARCHABLE_FIELDS]}
				searchPlaceholder="Search employee, role or job"
				containedHeight={{ maxHeight: 520 }}
				exportOptions={{ fileName: `recurring-${bucket}` }}
				columnPicker={{}}
				dataTestId={`recurring-candidates-${bucket}`}
				emptyState={<EmptyState title="Nobody in this bucket" hint="Every employee the run looked at landed somewhere else." />}
				getRowExpandContent={(row) => (detailEntries(row, t).length > 0 ? <CandidateDetail row={row} t={t} /> : null)}
				getExpandContentHeight={(row) => {
					const entries = detailEntries(row, t);
					return DETAIL_VERTICAL_PADDING + detailLineCount(row, t) * DETAIL_LINE_HEIGHT + Math.max(0, entries.length - 1) * DETAIL_ROW_GAP;
				}}
				searchValue={search}
				onSearchChange={setSearch}
				sortingOptions={isSkipped ? SKIPPED_SORT_OPTIONS : SCORED_SORT_OPTIONS}
				defaultSortState={isSkipped ? SKIPPED_DEFAULT_SORT : SCORED_DEFAULT_SORT}
				dataGridProps={{
					checkboxSelection: selectable,
					rowSelectionModel: selectable ? { type: "include", ids: new Set<string>(selectedIds ?? []) } : undefined,
					onRowSelectionModelChange: selectable
						? (model: GridRowSelectionModel) => {
								const emitted = model.type === "include" ? [...model.ids].map(String) : rows.map((row) => row.candidateId).filter((id) => !model.ids.has(id));
								// The emitted model is authoritative for the rows on screen ONLY. A header select-all
								// arrives already normalized to an `include` set over the searched rows, so it is
								// indistinguishable from "deselect everything else" — taking it literally silently
								// dropped every candidate the operator had already kept outside the current search.
								// So: keep prior picks that are currently filtered out, and let the model decide the
								// visible ones. With an empty search every row is visible and this is a plain replace.
								const visible = visibleIds;
								const kept = (selectedIds ?? []).filter((id) => !visible.has(id));
								onSelectionChange?.([...new Set([...kept, ...emitted.filter((id) => visible.has(id))])]);
							}
						: undefined,
				}}
			/>
			<RecurringShiftCalendarDialog row={calendarRow} onClose={() => setCalendarRow(null)} departmentId={departmentId} windowMonths={windowMonths} />
		</>
	);
};
